import { Lock } from 'lucide-react';
import { Badge } from '@/components/ui';
import type { EmploymentProfileCase } from '@/api/services/employment.service';
import type { HrCaseMemberRole, HrCaseStatus } from '@/api/services/hrCases.service';
import { CASE_STATUS, formatCaseDate, ROLE_LABELS } from './caseFormat';

/**
 * HR cases about this employee that the viewer works on or oversees. The
 * server lists no others, so an empty section is never rendered: its absence
 * says nothing about whether other cases exist.
 */
export function CaseProfileCard({ cases }: { cases: unknown }): React.ReactElement | null {
  const rows = Array.isArray(cases) ? (cases as EmploymentProfileCase[]) : [];
  if (rows.length === 0) return null;
  return (
    <section
      aria-labelledby="profile-hr-cases-heading"
      className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3
          id="profile-hr-cases-heading"
          className="text-base font-semibold text-gray-900 dark:text-gray-100"
        >
          HR cases
        </h3>
        <span className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          <Lock className="h-3.5 w-3.5" aria-hidden />
          Visible to you as a case team member or owner or admin
        </span>
      </div>
      <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-700">
        {rows.map((row) => {
          const status = CASE_STATUS[row.status as HrCaseStatus];
          return (
            <li key={row.caseId} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                  {row.kind === 'discipline' ? 'Disciplinary case' : 'Grievance raised'}{' '}
                  <span className="font-mono text-xs text-gray-600 dark:text-gray-300">
                    {row.reference}
                  </span>
                </span>
                <span className="text-xs text-gray-600 dark:text-gray-300">
                  Opened {formatCaseDate(row.openedAt)}
                  {row.myRoles.length > 0 &&
                    ` · You: ${row.myRoles
                      .map((role) => ROLE_LABELS[role as HrCaseMemberRole] ?? role)
                      .join(', ')}`}
                </span>
              </span>
              <Badge variant={status?.variant ?? 'neutral'} size="sm">
                {status?.label ?? row.status}
              </Badge>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
